import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/**
 * Deal-size band, picked with the toggle at the top right of every Portfolio Performance page. A deal's
 * ticket size is its financed amount; `undefined` means every size. The values are what the backend's
 * `ticket` query parameter takes (see portfolio-performance/app/utils/ticket.py). Same idea and values as
 * Risk Pricing Engine's own toggle.
 */
export type TicketSize = "micro" | "small" | "mid_market" | "large";

export const TICKET_SIZE_OPTIONS: ReadonlyArray<{ value: TicketSize; label: string; range: string }> = [
  { value: "micro", label: "Micro", range: "Under $25K" },
  { value: "small", label: "Small", range: "$25K to $250K" },
  { value: "mid_market", label: "Mid-market", range: "$250K to $5M" },
  { value: "large", label: "Large", range: "Over $5M" },
];

const STORAGE_KEY = "pp-ticket-size";

function readStored(): TicketSize | undefined {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return TICKET_SIZE_OPTIONS.find((option) => option.value === stored)?.value;
  } catch {
    return undefined;
  }
}

function writeStored(value: TicketSize | undefined) {
  try {
    if (value) window.sessionStorage.setItem(STORAGE_KEY, value);
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode / blocked storage: the selection just won't survive a reload.
  }
}

interface TicketSizeContextValue {
  ticket: TicketSize | undefined;
  setTicket: (value: TicketSize | undefined) => void;
}

const TicketSizeContext = createContext<TicketSizeContextValue>({
  ticket: undefined,
  setTicket: () => {},
});

/** One selection for the whole product, kept while moving between pages (and across a reload, per tab). */
export function TicketSizeProvider({ children }: { children: ReactNode }) {
  const [ticket, setTicketState] = useState<TicketSize | undefined>(readStored);
  const setTicket = useCallback((value: TicketSize | undefined) => {
    setTicketState(value);
    writeStored(value);
  }, []);
  const value = useMemo(() => ({ ticket, setTicket }), [ticket, setTicket]);
  return <TicketSizeContext.Provider value={value}>{children}</TicketSizeContext.Provider>;
}

export function useTicketSize() {
  return useContext(TicketSizeContext);
}
